import { cn } from "@/core/utils";
import type { ReactNode } from "react";
import { wobaText } from "./typography";

type DashboardPanelProps = {
  title?: ReactNode;
  description?: ReactNode;
  /** Selo ao lado do título (desktop) ou acima dele (mobile), ex.: Badge "Woba AI". */
  badge?: ReactNode;
  children?: ReactNode;
  /** Ação no rodapé do card (ex.: Button secundário de largura total). */
  footer?: ReactNode;
  className?: string;
  "data-testid"?: string;
};

/**
 * Card do Painel com cabeçalho título + descrição + selo e rodapé com ação.
 * O Card do Workbase DS não tem slot para selo no cabeçalho nem rodapé fixo,
 * então este frame reproduz os cards "Consumo elevado"/"Colaboradores" do Figma
 * (6087:23952, 6087:23905) só com tokens `--woba-*`.
 */
export function DashboardPanel({
  title,
  description,
  badge,
  children,
  footer,
  className,
  "data-testid": testId,
}: DashboardPanelProps) {
  return (
    <div
      className={cn(
        "flex flex-col justify-between gap-4 p-4 min-w-0 overflow-hidden border border-solid bg-[var(--woba-color-surface-default)] border-[color:var(--woba-color-stroke-default)] rounded-[var(--woba-border-radius-medium)]",
        className,
      )}
      data-testid={testId}
    >
      <div className="flex flex-col gap-4">
        {(title || description || badge) && (
          <div className="flex flex-col-reverse gap-2 items-start md:flex-row md:gap-4">
            <div className="flex flex-col flex-1 gap-1 min-w-0">
              {title && <h3 style={wobaText.headingSmall}>{title}</h3>}
              {description && (
                <div style={wobaText.captionMedium}>{description}</div>
              )}
            </div>
            {badge && <div className="shrink-0">{badge}</div>}
          </div>
        )}
        {children}
      </div>
      {footer}
    </div>
  );
}
